import {
  CheckmarkIcon,
  ChecklistIcon,
  DatabaseIcon,
  ProfileIcon,
  ProgressIcon,
  TimeClockIcon,
  UploadIcon,
} from '@design-systems/react-icons'
import { PERMISSIONS } from '../../../shared/roleAccess/config/roleConfig'

export const BRAND_DETAILS_TABS = Object.freeze([
  {
    icon: UploadIcon,
    id: 'upload',
    label: 'Upload Documents',
    permission: PERMISSIONS.VIEW_BRAND_DETAILS,
  },
  {
    icon: ChecklistIcon,
    id: 'drafts',
    label: 'Drafts',
    permission: PERMISSIONS.VIEW_BRAND_DETAILS,
  },
  {
    icon: ProfileIcon,
    id: 'profile',
    label: 'Brand Profile',
    permission: PERMISSIONS.VIEW_BRAND_DETAILS,
  },
  {
    icon: DatabaseIcon,
    id: 'knowledge',
    label: 'Knowledge Base',
    permission: PERMISSIONS.VIEW_BRAND_DETAILS,
  },
])

// Document types are managed in Settings and served by /document/types. The
// API carries no "required" flag, so every active type counts toward the
// extraction gate except the catch-all types named here.
export const OPTIONAL_DOCUMENT_TYPE_NAMES = Object.freeze(['OTHER', 'OTHERS'])

export const toDocumentTypeOptions = (documentTypes = []) =>
  documentTypes.map((documentType) => ({
    id: documentType.id,
    label: documentType.label || documentType.displayName || documentType.name,
    name: documentType.name,
    required: documentType.required === true,
  }))

// UPLOADING, SAVING and FAILED describe an in-flight browser upload. The rest
// are BrandDNADocumentStatus values from /brands/{brandUuid}/details.
export const DOCUMENT_STATUS = Object.freeze({
  ACTIVE: 'ACTIVE',
  APPROVED: 'APPROVED',
  AWAITING_PUBLISH: 'AWAITING_PUBLISH',
  EXTRACTING: 'EXTRACTING',
  EXTRACTION_FAILURE: 'EXTRACTION_FAILURE',
  FAILED: 'FAILED',
  IN_REVIEW: 'IN_REVIEW',
  NOT_UPLOADED: 'NOT_UPLOADED',
  REJECTED: 'REJECTED',
  SAVING: 'SAVING',
  SUPERSEDED: 'SUPERSEDED',
  UPLOADED: 'UPLOADED',
  UPLOADING: 'UPLOADING',
})

// Furthest along first. Both document-type rollups read this order: the upload
// rail takes the most advanced status among a type's documents, the drafts rail
// takes the least advanced one.
export const DOCUMENT_STATUS_PRECEDENCE = Object.freeze([
  DOCUMENT_STATUS.ACTIVE,
  DOCUMENT_STATUS.AWAITING_PUBLISH,
  DOCUMENT_STATUS.APPROVED,
  DOCUMENT_STATUS.IN_REVIEW,
  DOCUMENT_STATUS.EXTRACTING,
  DOCUMENT_STATUS.UPLOADED,
  DOCUMENT_STATUS.EXTRACTION_FAILURE,
  DOCUMENT_STATUS.REJECTED,
  DOCUMENT_STATUS.SUPERSEDED,
])

// The base reading of a status. The rail refines UPLOADED further — see
// getRailStatusLabel — because what "uploaded" means to the reviewer depends
// on whether they are still staging documents or waiting on extraction.
export const DOCUMENT_STATUS_LABELS = Object.freeze({
  [DOCUMENT_STATUS.ACTIVE]: 'Active',
  [DOCUMENT_STATUS.APPROVED]: 'Approved',
  [DOCUMENT_STATUS.AWAITING_PUBLISH]: 'Awaiting publish',
  [DOCUMENT_STATUS.EXTRACTING]: 'Extraction in progress',
  [DOCUMENT_STATUS.EXTRACTION_FAILURE]: 'Extraction failed',
  [DOCUMENT_STATUS.FAILED]: 'Upload failed',
  [DOCUMENT_STATUS.IN_REVIEW]: 'Queued for review',
  [DOCUMENT_STATUS.NOT_UPLOADED]: 'Not uploaded',
  [DOCUMENT_STATUS.REJECTED]: 'Rejected',
  [DOCUMENT_STATUS.SAVING]: 'Saving to Brand DNA…',
  [DOCUMENT_STATUS.SUPERSEDED]: 'Superseded',
  [DOCUMENT_STATUS.UPLOADED]: 'Uploaded',
  [DOCUMENT_STATUS.UPLOADING]: 'Uploading',
})

export const getDocumentStatusLabel = (status) =>
  DOCUMENT_STATUS_LABELS[status] ||
  DOCUMENT_STATUS_LABELS[DOCUMENT_STATUS.NOT_UPLOADED]

// How a status reads at a glance: green once the document is accepted, blue
// while it moves through upload, extraction, and review, orange when the type
// is still empty, red when it needs someone to act, grey when it no longer
// counts either way.
export const DOCUMENT_STATUS_TONES = Object.freeze({
  [DOCUMENT_STATUS.ACTIVE]: 'approved',
  [DOCUMENT_STATUS.APPROVED]: 'approved',
  [DOCUMENT_STATUS.AWAITING_PUBLISH]: 'warning',
  [DOCUMENT_STATUS.EXTRACTING]: 'progress',
  [DOCUMENT_STATUS.EXTRACTION_FAILURE]: 'error',
  [DOCUMENT_STATUS.FAILED]: 'error',
  [DOCUMENT_STATUS.IN_REVIEW]: 'progress',
  [DOCUMENT_STATUS.NOT_UPLOADED]: 'warning',
  [DOCUMENT_STATUS.REJECTED]: 'error',
  [DOCUMENT_STATUS.SAVING]: 'progress',
  [DOCUMENT_STATUS.SUPERSEDED]: 'neutral',
  [DOCUMENT_STATUS.UPLOADED]: 'progress',
  [DOCUMENT_STATUS.UPLOADING]: 'progress',
})

// An unrecognized status reads as an empty type, matching getDocumentStatusLabel.
export const getDocumentStatusTone = (status) =>
  DOCUMENT_STATUS_TONES[status] ||
  DOCUMENT_STATUS_TONES[DOCUMENT_STATUS.NOT_UPLOADED]

// What sits inside the rail's status circle. Anything absent here — an empty
// document type, a failed upload — leaves the circle blank.
export const DOCUMENT_STATUS_ICONS = Object.freeze({
  [DOCUMENT_STATUS.ACTIVE]: CheckmarkIcon,
  [DOCUMENT_STATUS.APPROVED]: CheckmarkIcon,
  [DOCUMENT_STATUS.AWAITING_PUBLISH]: CheckmarkIcon,
  [DOCUMENT_STATUS.EXTRACTING]: ProgressIcon,
  [DOCUMENT_STATUS.IN_REVIEW]: CheckmarkIcon,
  [DOCUMENT_STATUS.UPLOADED]: TimeClockIcon,
})

export const getDocumentStatusIcon = (status) =>
  DOCUMENT_STATUS_ICONS[status] || null

// A rejected or superseded version leaves its document type unsatisfied.
export const isDocumentUploaded = (status) =>
  [
    DOCUMENT_STATUS.ACTIVE,
    DOCUMENT_STATUS.APPROVED,
    DOCUMENT_STATUS.AWAITING_PUBLISH,
    DOCUMENT_STATUS.EXTRACTING,
    DOCUMENT_STATUS.IN_REVIEW,
    DOCUMENT_STATUS.UPLOADED,
  ].includes(status)

// Queued for publish still counts as approved — the reviewer is done with it,
// only an admin's publish is outstanding.
export const isDocumentApproved = (status) =>
  [
    DOCUMENT_STATUS.ACTIVE,
    DOCUMENT_STATUS.APPROVED,
    DOCUMENT_STATUS.AWAITING_PUBLISH,
  ].includes(status)

// How far extraction has got is no longer inferred from document statuses —
// the extraction status endpoint answers it directly. See brandLifecycleConfig.

export const BRAND_PROFILE_SECTION_COUNT = 16
